import './modal.scss';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

class Modal {
    constructor (element, options) {
        const defaults = {
            initAttr: 'data-modal',
            modalAttr: 'data-modaltarget',
            scrollerAttr: 'scroller',
            onAjaxLoaded: null,
            onClosed: null,
            loader: null
        };

        this.settings = Object.assign({}, defaults, options);
        this.$modal = element;
        this.modalOpen = false;
        this.$modalTarget = null;
        this.$modalScroller = null;
        this.movedModalToMainLayout = false;
        this.modalIframeURL = this.$modal.getAttribute('data-iframe-url');
        this.$fullImageSrc = this.$modal.dataset.modalImageSrc;
        this.fetchJson = this.$modal.dataset.modalContentJson;
        this.$modalContentBlob = null;
        this.$$modalClose = null;

        const modalId = this.$modal.getAttribute('data-modal');

        if (modalId) {
            this.$modalTarget = document.getElementById(modalId);
            this.$modalContentBlob = this.$modalTarget.querySelector('[data-modal-content-blob]');

            if (this.$modalTarget) {
                this.$$modalClose = this.$modalTarget.querySelectorAll('[data-modal-close]');
            }

            if (this.$fullImageSrc) {
                this.$fullsizeImage = this.$modalTarget.querySelector('img');
            }
        }
    }

    initialize () {
        this.setEvents();

        if (this.$modalTarget !== null) {
            this.movedModalToMainLayout = this.$modalTarget.getAttribute('data-modal-move');
        }

        if (this.movedModalToMainLayout === 'true') {
            const $modalBlob = document.getElementById('modal-blob');
            if ($modalBlob) {
                const $modal = $modalBlob.querySelector(`#${this.$modalTarget.id}`);

                if (!$modal) {
                    $modalBlob.appendChild(this.$modalTarget);
                }
            }
        }
    }

    setEvents () {
        this.$modal.setAttribute('data-modal-init', 'true');

        this.$modal.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal();
        });

        if (this.$modal.getAttribute('data-modal-open') !== null) {
            window.setTimeout(() => {
                this.openModal();
            }, 500);
        }

        // open modal on enter or space key
        this.$modal.addEventListener('keydown', (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault();
                this.openModal();
            }
        });

        // close modal on ESC key
        document.addEventListener('keydown', (e) => {
            let isEscape = false;
            if ('key' in e) {
                isEscape = (e.key === 'Escape' || e.key === 'Esc');
            } else {
                isEscape = (e.keyCode === 27);
            }
            if (isEscape && this.modalOpen === true) {
                this.closeModal();
            }
        });

        // close modal on tab out
        document.addEventListener('keydown', (event) => {
            if (event.code === 'Tab' || event.code === 'Tabs') {
                this.handleOutsideClick(event);
            }
        });

        // close modal on click of a close button or the shadow element
        if (this.$$modalClose.length > 0) {
            this.$$modalClose.forEach(($close) => {
                $close.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (this.modalOpen === true) {
                        this.closeModal();
                    }
                });
            });
        }
    }

    openModal () {
        this.$modal.setAttribute('aria-pressed', 'true');

        if (this.$modalTarget) {
            if (this.fetchJson !== undefined && this.$modalContentBlob !== undefined) {
                this.fetchContentFromJson();
            } else {
                if (this.$fullsizeImage && this.$fullImageSrc) {
                    this.setImageSource();
                }

                this.showModal();
            }
        }
    }

    fetchContentFromJson () {
        this.$modalContentBlob.innerHTML = '';
        this.$modalContentBlob.innerHTML = JSON.parse(this.fetchJson);

        window.dispatchEvent(new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: this.$modalContentBlob
            }
        }));

        this.showModal();
    }

    setImageSource () {
        if (this.$fullsizeImage.src.indexOf(this.$fullImageSrc) === -1) {
            this.$fullsizeImage.src = '';
        }
        this.$fullsizeImage.src = this.$fullImageSrc;
    }

    showModal () {
        this.$modalScroller = this.$modalTarget.querySelector('[' + this.settings.modalAttr + '="' + this.settings.scrollerAttr + '"]');
        this.$modalTarget.classList.add('is--active');
        this.$modalTarget.setAttribute('aria-hidden', 'false');

        const $modalIframeBlob = this.$modalTarget.querySelector('[data-modaltarget-iframe-blob]');
        if ($modalIframeBlob && this.modalIframeURL) {
            $modalIframeBlob.innerHTML = `<iframe src="${this.modalIframeURL}" tabindex="0" title="Externer inhalt" frameborder="0"></iframe>`;

            const $iframe = $modalIframeBlob.querySelector('iframe');

            setTimeout(() => {
                $iframe.focus();
            }, 100);
        } else {
            setTimeout(() => {
                this.$modalTarget.querySelector('[data-modal-close-btn]').focus();
            }, 100);
        }

        if (this.$modalScroller) {
            disableBodyScroll(this.$modalScroller);
        }

        this.modalOpen = true;
        this.$modalTarget.API = this;
    }

    closeModal () {
        this.$modalTarget.classList.remove('is--active');

        const $modalIframeBlob = this.$modalTarget.querySelector('[data-modaltarget-iframe-blob]');
        if ($modalIframeBlob) {
            $modalIframeBlob.innerHTML = '';
        }

        setTimeout(() => {
            this.$modalTarget.setAttribute('aria-hidden', 'true');
        }, 300);

        if (this.$modalScroller) {
            enableBodyScroll(this.$modalScroller);
        }

        this.modalOpen = false;

        if (typeof this.settings.onClosed === 'function') {
            this.settings.onClosed(this.$modalTarget);
        }

        this.$modal.setAttribute('aria-pressed', 'false');
        this.$modal.focus();

        const modelClosed = new CustomEvent('modal.closed', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: this.$modalTarget
            }
        });

        window.dispatchEvent(modelClosed);
    }

    handleOutsideClick (e) {
        if (!this.$modalTarget.contains(e.target) && this.modalOpen) {
            this.closeModal();
        }
    }
}

export { Modal };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$modal = $context.querySelectorAll('[data-modal]:not([data-modal-init="true"])');

        for (let i = 0; i < $$modal.length; i++) {
            const modal = new Modal($$modal[i]);
            modal.initialize();
        }
    }
});
